import { 
  Employee, 
  ShiftType,
  DailyAvailability,
  EmployeeAvailabilityResponse,
  CreateEmployeeRequest, 
  SetDayAvailabilityRequest,
  UpdateAvailabilityRequest,
  AvailabilityDateRange,
  UserInfo,
  SchedulePeriod,
  ApiResponse 
} from '../types';

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class ApiService {
  private async fetchWithErrorHandling<T>(
    url: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      const data: ApiResponse<T> = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        errors: [error instanceof Error ? error.message : 'Unknown error']
      };
    }
  }

  // Employee API
  async getEmployees(): Promise<ApiResponse<Employee[]>> {
    return this.fetchWithErrorHandling<Employee[]>('/employees');
  }

  async getEmployeeById(id: number): Promise<ApiResponse<Employee>> {
    return this.fetchWithErrorHandling<Employee>(`/employees/${id}`);
  }

  async getCurrentEmployee(): Promise<ApiResponse<UserInfo>> {
    return this.fetchWithErrorHandling<UserInfo>('/employees/current');
  }

  // Schedule Period API
  async getCurrentSchedulePeriod(): Promise<ApiResponse<SchedulePeriod | null>> {
    return this.fetchWithErrorHandling<SchedulePeriod | null>('/schedule-periods/current');
  }

  async createEmployee(employeeData: CreateEmployeeRequest): Promise<ApiResponse<Employee>> {
    return this.fetchWithErrorHandling<Employee>('/employees', {
      method: 'POST',
      body: JSON.stringify(employeeData),
    });
  }

  // Shift Types API - Get available shift types (morning, evening)
  async getShiftTypes(): Promise<ApiResponse<ShiftType[]>> {
    return this.fetchWithErrorHandling<ShiftType[]>('/shift-types');
  }

  // NEW: Date-specific Availability API
  async getEmployeeAvailabilityForDateRange(
    employeeId: number, 
    startDate: string, // "YYYY-MM-DD"
    endDate: string    // "YYYY-MM-DD"
  ): Promise<ApiResponse<EmployeeAvailabilityResponse>> {
    const params = new URLSearchParams({
      startDate,
      endDate
    });
    return this.fetchWithErrorHandling<EmployeeAvailabilityResponse>(
      `/employees/${employeeId}/availability?${params.toString()}`
    );
  }

  async setDayAvailability(
    employeeId: number, 
    date: string, // "YYYY-MM-DD"
    availability: SetDayAvailabilityRequest
  ): Promise<ApiResponse<DailyAvailability[]>> {
    return this.fetchWithErrorHandling<DailyAvailability[]>(
      `/employees/${employeeId}/availability/${date}`, 
      {
        method: 'PUT',
        body: JSON.stringify(availability),
      }
    );
  }

  async updateEmployeeAvailability(
    employeeId: number,
    availability: UpdateAvailabilityRequest
  ): Promise<ApiResponse<DailyAvailability[]>> {
    return this.fetchWithErrorHandling<DailyAvailability[]>(
      `/employees/${employeeId}/availability`,
      {
        method: 'PUT',
        body: JSON.stringify(availability),
      }
    );
  }

  // Calendar utility methods for date range management
  async getAvailabilityDateRange(): Promise<AvailabilityDateRange> {
    try {
      // First try to get the current schedule period
      const currentPeriodResponse = await this.getCurrentSchedulePeriod();
      
      if (currentPeriodResponse.success && currentPeriodResponse.data) {
        // Use the current schedule period dates
        return {
          startDate: currentPeriodResponse.data.startDate,
          endDate: currentPeriodResponse.data.endDate
        };
      }
    } catch (error) {
      console.warn('Failed to fetch current schedule period, falling back to default calculation:', error);
    }
    
    // Fallback: Calculate default 14-day period starting from next Monday
    const today = new Date();
    const daysUntilMonday = (8 - today.getDay()) % 7 || 7;
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + daysUntilMonday);
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 13); // 14 days total
    
    return {
      startDate: this.formatDateForAPI(startDate),
      endDate: this.formatDateForAPI(endDate)
    };
  }

  // Bulk availability operations
  async setBulkAvailability(
    employeeId: number,
    dateRange: AvailabilityDateRange,
    shiftTypeIds: number[], // [] for none, [1] for morning, [2] for evening, [1,2] for both
    notes?: string
  ): Promise<ApiResponse<DailyAvailability[]>> {
    return this.fetchWithErrorHandling<DailyAvailability[]>(
      `/employees/${employeeId}/availability/bulk`, 
      {
        method: 'PUT',
        body: JSON.stringify({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          shiftTypeIds,
          notes
        }),
      }
    );
  }

  // Utility methods
  formatDateForAPI(date: Date): string {
    // Return "YYYY-MM-DD" format for API consistency
    return date.toISOString().split('T')[0];
  }

  formatDisplayDate(dateString: string): string {
    // Convert "YYYY-MM-DD" to user-friendly format
    const date = new Date(dateString + 'T00:00:00'); // Avoid timezone issues
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateRange(startDate: string, endDate: string): string {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    
    if (start.getFullYear() === end.getFullYear()) {
      if (start.getMonth() === end.getMonth()) {
        // Same month: "Aug 4-17, 2025"
        return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()}-${end.getDate()}, ${start.getFullYear()}`;
      } else {
        // Different months, same year: "Aug 28 - Sep 10, 2025"
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${start.getFullYear()}`;
      }
    } else {
      // Different years: "Dec 30, 2024 - Jan 12, 2025"
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
  }

  getDayOfWeek(dateString: string): number {
    // Return 0=Sunday, 1=Monday, etc.
    const date = new Date(dateString + 'T00:00:00');
    return date.getDay();
  }

  isWeekend(dateString: string): boolean {
    const dayOfWeek = this.getDayOfWeek(dateString);
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  }

  isPastDate(dateString: string): boolean {
    const date = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }

  isTooFarFuture(dateString: string, maxDays: number = 90): boolean {
    const date = new Date(dateString + 'T00:00:00');
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + maxDays);
    return date > maxDate;
  }
}

export const apiService = new ApiService();
